import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import {
  GridHeaderColumn,
  GridHeaderColumnAriaLabelProcessingParams,
  GridHeaderColumnIconTypeProcessingParams,
} from "@webapp/home/components/grid-header/grid-header.models";
import { localize } from "@webapp/localization/utils/localization.utils";

/**
 * @example <grid-header [columns]="columns" [(collapsed)]="isCollapsed"></grid-header>
 *
 * @param columns {GridHeaderColumn} - The definition of the columns to show. The width of the columns is controlled by the flex property
 * @param expanded {boolean} - Control over the expanded state
 * @param firstColumnPrefix {string} - Just because the team OKR have the name in the beginning of the 1st column. If it's a function to return the columns array it gets called 140 times for a single team
 */
@Component({
  selector: "grid-header",
  templateUrl: "./grid-header.component.html",
  styleUrls: ["./grid-header.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridHeaderComponent implements OnInit, OnChanges {
  @Input() public columns: GridHeaderColumn[];
  @Input() public expanded: boolean;
  @Input() public firstColumnPrefix: string;

  @Output() public readonly expandedChange = new EventEmitter<boolean>();

  public iconType: string;
  public ariaLabel: string;

  public ngOnInit(): void {
    this.setIconType({ expanded: this.expanded });
    this.setAriaLabel({ expanded: this.expanded, columns: this.columns, firstColumnPrefix: this.firstColumnPrefix });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const expanded = changes.expanded?.currentValue ?? this.expanded;
    const columns = changes.columns?.currentValue ?? this.columns;
    const firstColumnPrefix = changes.firstColumnPrefix?.currentValue ?? this.firstColumnPrefix;

    this.setIconType({ expanded });
    this.setAriaLabel({ expanded, columns, firstColumnPrefix });
  }

  public toggleCollapse(): void {
    this.expandedChange.emit(!this.expanded);
  }

  private setIconType({ expanded }: GridHeaderColumnIconTypeProcessingParams): void {
    this.iconType = expanded ? "minus" : "plus";
  }

  private setAriaLabel({ expanded, columns, firstColumnPrefix = "" }: GridHeaderColumnAriaLabelProcessingParams): void {
    const action = expanded ? localize("collapse") : localize("expand");

    const columnName = `${firstColumnPrefix}${columns[0]?.label ?? ""}`;

    this.ariaLabel = `${action} ${columnName}`;
  }
}
